import type { CompactLexicon, Lang, LexiconSet } from '@polyglot/engine';
import { Lexicon } from '@polyglot/engine';

const BASE_URL = '/lexicons';

interface LexiconIndex {
  current: Partial<Record<Lang, string>>;
}

let indexPromise: Promise<LexiconIndex> | null = null;
const loaded = new Map<string, Promise<Lexicon>>();
let worker: Worker | null = null;
let nextId = 1;
const pending = new Map<
  number,
  { resolve: (c: CompactLexicon) => void; reject: (e: Error) => void }
>();

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(new URL('./lexicon.worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (
      ev: MessageEvent<{ id: number; ok: boolean; compact?: CompactLexicon; error?: string }>,
    ) => {
      const p = pending.get(ev.data.id);
      if (!p) return;
      pending.delete(ev.data.id);
      if (ev.data.ok && ev.data.compact) p.resolve(ev.data.compact);
      else p.reject(new Error(ev.data.error ?? 'lexicon load failed'));
    };
  }
  return worker;
}

export function lexiconIndex(): Promise<LexiconIndex> {
  if (!indexPromise) {
    indexPromise = fetch(`${BASE_URL}/index.json`).then(async (r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return (await r.json()) as LexiconIndex;
    });
    indexPromise.catch(() => {
      indexPromise = null;
    });
  }
  return indexPromise;
}

/** Loads (once) the lexicon of a language, at a specific version or the current one. */
export async function loadLexicon(lang: Lang, version?: string): Promise<Lexicon> {
  const v = version ?? (await lexiconIndex()).current[lang];
  if (!v) throw new Error(`No lexicon for ${lang}`);
  const cacheKey = `${lang}@${v}`;
  let p = loaded.get(cacheKey);
  if (!p) {
    p = new Promise<CompactLexicon>((resolve, reject) => {
      const id = nextId++;
      pending.set(id, { resolve, reject });
      getWorker().postMessage({ id, lang, version: v, baseUrl: BASE_URL });
    }).then((c) => Lexicon.fromCompact(c));
    loaded.set(cacheKey, p);
    p.catch(() => loaded.delete(cacheKey));
  }
  return p;
}

export async function loadLexicons(
  langs: readonly Lang[],
  versions?: Partial<Record<Lang, string>>,
): Promise<LexiconSet> {
  const out: LexiconSet = {};
  await Promise.all(
    langs.map(async (l) => {
      out[l] = await loadLexicon(l, versions?.[l]);
    }),
  );
  return out;
}
